// src/hooks/useGameAvailability.js
// ============================================================================
// Hook to check which games are enabled/disabled AND visible for the user.
//
// Each game can have:
//   enabled: true/false  (original toggle - controls event creation)
//   visibility: 'live' | 'testing' | 'hidden'
//     - live:    visible to everyone (default if field missing)
//     - testing: visible to admins only
//     - hidden:  visible to nobody
//
// Falls back to all games enabled + live if Firestore document doesn't exist.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../../firebaseConfig';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { GAME_PROFILES } from '../constants/gameData';

export function useGameAvailability() {
  const [games, setGames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if current user is admin (one-time on mount)
  useEffect(() => {
    const checkAdmin = async () => {
      const user = auth.currentUser;
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().role === 'admin') {
          setIsAdmin(true);
        }
      } catch (err) {
        console.warn('useGameAvailability: Admin check failed:', err.message);
      }
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    // Initialize with all games enabled + live by default from GAME_PROFILES
    const defaultGames = {};
    Object.keys(GAME_PROFILES).forEach(gameKey => {
      defaultGames[gameKey] = {
        enabled: true,
        visibility: 'live',
        displayName: GAME_PROFILES[gameKey]?.displayName || gameKey,
        reason: '',
      };
    });

    const docRef = doc(db, 'config', 'gameAvailability');
    
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Merge Firestore data with defaults
          const mergedGames = { ...defaultGames };
          if (data.games) {
            Object.keys(data.games).forEach(gameKey => {
              if (mergedGames[gameKey]) {
                mergedGames[gameKey] = {
                  ...mergedGames[gameKey],
                  ...data.games[gameKey],
                  // Ensure visibility has a default
                  visibility: data.games[gameKey]?.visibility || 'live',
                };
              }
            });
          }
          setGames(mergedGames);
        } else {
          // Document doesn't exist - use defaults (all enabled + live)
          setGames(defaultGames);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.warn('useGameAvailability: Error loading, using defaults:', err.message);
        setGames(defaultGames);
        setLoading(false);
        setError(err.message);
      }
    );

    return () => unsubscribe();
  }, []);

  // Check if a specific game is enabled AND visible to the current user
  const isGameEnabled = useCallback((gameKey) => {
    if (!games[gameKey]) return true; // Unknown game defaults to enabled

    const game = games[gameKey];

    // Check enabled flag first (original behavior)
    if (game.enabled === false) return false;

    // Check visibility
    const visibility = game.visibility || 'live';

    if (visibility === 'live') return true;
    if (visibility === 'testing') return isAdmin;
    if (visibility === 'hidden') return false;

    // Fallback
    return true;
  }, [games, isAdmin]);

  // Check if a game's EVENTS should be shown to the current user.
  // Visibility-only — distinct from isGameEnabled: the enabled flag blocks
  // event creation, but a disabled live game's existing events still render.
  //   live    -> everyone
  //   testing -> admins only
  //   hidden  -> nobody
  const isGameVisible = useCallback((gameKey) => {
    const key = String(gameKey || '').toLowerCase();
    const visibility = games[key]?.visibility || 'live';

    if (visibility === 'testing') return isAdmin;
    if (visibility === 'hidden') return false;
    return true;
  }, [games, isAdmin]);

  // Event docs carry their game as `game` (string key), `game.id` (mapped
  // info object), or `gameId` — accept all three shapes. Unknown game
  // defaults to visible, matching isGameEnabled's default.
  const isEventVisible = useCallback((event) => {
    const raw = event?.game;
    const gameKey = (raw && typeof raw === 'object' ? raw.id : raw) || event?.gameId || '';
    if (!gameKey) return true;
    return isGameVisible(gameKey);
  }, [isGameVisible]);

  // Filter an array of event docs by their game's visibility
  const filterVisibleEvents = useCallback((events) => {
    return events.filter(isEventVisible);
  }, [isEventVisible]);

  // Check if a game is in testing mode (for admin UI badges)
  const isGameTesting = useCallback((gameKey) => {
    return games[gameKey]?.visibility === 'testing';
  }, [games]);

  // Get the visibility value for a game
  const getGameVisibility = useCallback((gameKey) => {
    return games[gameKey]?.visibility || 'live';
  }, [games]);

  // Get display name for a game
  const getDisplayName = useCallback((gameKey) => {
    return games[gameKey]?.displayName || GAME_PROFILES[gameKey]?.displayName || gameKey;
  }, [games]);

  // Get disable reason for a game
  const getDisableReason = useCallback((gameKey) => {
    return games[gameKey]?.reason || '';
  }, [games]);

  // Filter an array of game keys to only enabled + visible games
  const filterEnabledGames = useCallback((gameKeys) => {
    return gameKeys.filter(isGameEnabled);
  }, [isGameEnabled]);

  return {
    games,
    loading,
    error,
    isAdmin,
    isGameEnabled,
    isGameVisible,
    isEventVisible,
    filterVisibleEvents,
    isGameTesting,
    getGameVisibility,
    getDisplayName,
    getDisableReason,
    filterEnabledGames,
  };
}

export default useGameAvailability;